import { Divider, Grid, Typography } from '@material-ui/core';
import React from 'react';
import { FieldValue } from '../../components/common/presentation';
import { ageInYears, demographicsDateTimeFormat, formatDateTime, formatLocalDate } from '../date-time';
import { truncateSpan } from '../format';
import { DemographicsDocument } from './document-types';

function VField({ label, value, style = undefined, truncate = NaN }) {
  return (
    <Grid container direction="column" style={style}>
      <Grid item>
        <Typography
          style={{ whiteSpace: 'nowrap' }}
          variant="body2"
          color="textSecondary"
        >
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <Typography color="textPrimary">
          {isNaN(truncate) ? value : truncateSpan(value, truncate + 'px')}
        </Typography>
      </Grid>
    </Grid>
  );
}

function extractCustomFieldValues(a: any[]) {
  if (!a?.length) {
    return '';
  }

  const isString = typeof a[0] === 'string';

  if (isString) {
    return a.join(', ');
  }

  const entries = a.map((v) => Object.entries(v));
  return entries.map(([k, v]) => `${k}: ${v || 'NA'}`).join(',\n');
}
export function DemographicsRenderer(ctx: DemographicsDocument & any) {
  // Synthesize patient if missing but root-level fields exist
  let { patient, disposition, accountNumber, mrn, registrationDate, dischargeDate, insurances, customFieldsArray, guarantor, attending } = ctx;

  if (!patient && (ctx.firstName || ctx.lastName || ctx.gender || ctx.birthDate)) {
    patient = {
      firstName: ctx.firstName || '',
      lastName: ctx.lastName || '',
      gender: ctx.gender || '',
      birthDate: ctx.birthDate || '',
    };
  }

  const firstName = patient?.firstName || '';
  const lastName = patient?.lastName || '';
  const birthDate = patient?.birthDate || '';
  const gender = patient?.gender || '';

  const ins = (insurances || []).map((v) => v?.companyName).join(', ');
  const divider = (
    <div style={{ padding: '12px 0px 12px 0px' }}>
      <Divider orientation="horizontal" style={{ width: '100%' }} />
    </div>
  );

  return (
    <div style={{ width: '100%', height: '83vh', padding: '12px 24px 0 24px' }}>
      <FieldValue label="Patient" value={lastName + ', ' + firstName} />
      {divider}
      <Grid
        container
        style={{ paddingTop: 12, flexWrap: 'nowrap', width: '100%' }}
        direction="row"
        spacing={2}
        justify="space-between"
        alignItems="center"
      >
        <FieldValue label="Account" value={accountNumber} />
        <VField label="MRN" value={mrn} truncate={67} />
        <FieldValue
          label="Date of Birth"
          value={birthDate ? formatLocalDate(birthDate) : ''}
        />
        <FieldValue
          label="Age"
          value={birthDate ? ageInYears(birthDate) : ''}
        />
        <FieldValue label="Gender" value={gender.charAt(0)} />
      </Grid>

      {divider}
      <Grid
        container
        style={{ paddingTop: 12, paddingLeft: 8, flexWrap: 'nowrap' }}
        direction="row"
        spacing={2}
        justify="center"
        alignItems="center"
      >
        <VField
          label="Registration Date / Time"
          value={formatDateTime(registrationDate, demographicsDateTimeFormat)}
        />
        {dischargeDate && (
          <VField label="Discharge Date / Time" value={formatDateTime(dischargeDate, demographicsDateTimeFormat)} />
        )}
        <FieldValue label="Disposition" value={disposition} />
      </Grid>
      {divider}
      <Grid
        container
        style={{ paddingTop: 12, flexWrap: 'nowrap', width: '100%' }}
        direction="row"
        spacing={2}
        alignItems="center"
      >
        <VField label="Insurance" value={ins} style={{ paddingLeft: 10 }} />
        {guarantor && (
          <VField
            label="Guarantor"
            value={(guarantor.lastName || '') + ', ' + (guarantor.firstName || '')}
          />
        )}
      </Grid>

      {attending && (
        <React.Fragment>
          {divider}
          <Grid
            container
            style={{ paddingTop: 12, flexWrap: 'nowrap', width: '100%' }}
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <VField
              style={{ paddingLeft: 10 }}
              label="Attending"
              value={(attending.lastName || '') + ', ' + (attending.firstName || '')}
            />
          </Grid>
        </React.Fragment>
      )}

      <Grid item style={{ height: 12 }}>
        {' '}
      </Grid>
      {divider}
      {customFieldsArray?.length ? (
        <VField
          label="Custom Fields"
          value={extractCustomFieldValues(customFieldsArray)}
        />
      ) : null}
    </div>
  );
}
